import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GroupsIcon from "@mui/icons-material/Groups";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";

export function fmtMoney(n) {
  if (!isFinite(n)) return "$0";
  return `$${Math.round(n).toLocaleString()}`;
}

export function fmtPct(n, digits = 1) {
  if (!isFinite(n)) return "0%";
  return `${n.toFixed(digits)}%`;
}

export function fmtHours(n) {
  if (!isFinite(n)) return "0.00 hrs";
  return `${n.toFixed(2)} hrs`;
}

export function fmtHoursShort(n) {
  if (!isFinite(n)) return "0.00";
  return n.toFixed(2);
}

export function fmtSignedPct(n, digits = 1) {
  if (!isFinite(n) || Math.abs(n) < 0.05) return "On forecast";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}% vs forecast`;
}

export function fmtSignedPctVs(n, label, digits = 1) {
  if (!isFinite(n) || Math.abs(n) < 0.05) return `On ${label}`;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}% vs ${label}`;
}

export function varianceColorClass(n) {
  if (!isFinite(n) || Math.abs(n) < 0.05) return "text-slate-400";
  return n > 0 ? "text-emerald-600" : "text-rose-600";
}

export function costVarianceColorClass(n) {
  if (!isFinite(n) || Math.abs(n) < 0.05) return "text-slate-400";
  return n > 0 ? "text-rose-600" : "text-emerald-600";
}

export function fmtMoneyCents(n) {
  if (!isFinite(n)) return "$0.00";
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function fmtNumber(n) {
  if (!isFinite(n)) return "0";
  return Math.round(n).toLocaleString();
}

export function fmtSplh(n) {
  if (!isFinite(n)) return "$0/hr";
  return `${fmtMoney(n)}/hr`;
}

export function fmtTplh(n) {
  if (!isFinite(n)) return "0/hr";
  return `${n.toFixed(1)}/hr`;
}

export function fmtSignedPp(n, label, digits = 1) {
  if (!isFinite(n) || Math.abs(n) < 0.05) return `On ${label}`;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}pp vs ${label}`;
}

export const SUMMARY_CARD_REGISTRY = {
  sales: {
    id: "sales",
    label: "Sales",
    icon: AttachMoneyIcon,
    actualGated: null,
    planned: {
      cardLabel: () => "Forecasted Sales",
      value: (m) => fmtMoney(m.weekSales),
      sub: () => "Week Total",
      sparkline: (m) => m.dailySales,
      sparklineType: "line",
      sparklineTooltip: {
        kind: "value",
        formatter: (m, i) => fmtMoney(m.dailySales[i]),
        suffix: "forecasted",
      },
    },
    actual: {
      cardLabel: () => "Actual Sales",
      value: (m) =>
        m.isFullPastWeek
          ? fmtMoney(m.actualSalesSoFar)
          : `${fmtMoney(m.actualSalesSoFar)} / ${fmtMoney(m.projectedSales)}`,
      sub: (m) =>
        m.isFullPastWeek
          ? fmtSignedPct(m.salesVariance)
          : `${fmtSignedPct(m.salesVariance)} (projected)`,
      subColorClass: (m) => varianceColorClass(m.salesVariance),
      sparkline: (m) => m.dailyHybridSales,
      sparklineType: "line",
      sparklineTooltip: {
        kind: "value",
        formatter: (m, i) => fmtMoney(m.dailyHybridSales[i]),
        suffix: "actual",
      },
      hasPastBoundary: true,
    },
  },

  hours_scheduled: {
    id: "hours_scheduled",
    label: "Scheduled vs Used Hours",
    icon: GroupsIcon,
    actualGated: null,
    planned: {
      cardLabel: () => "Scheduled Hours",
      value: (m) => fmtHours(m.weekScheduledHours),
      sub: () => "Week Total",
      sparkline: (m) => m.dailyScheduledHours,
      sparklineType: "line",
      sparklineTooltip: {
        kind: "value",
        formatter: (m, i) => fmtHours(m.dailyScheduledHours[i]),
        suffix: "scheduled",
      },
    },
    actual: {
      cardLabel: () => "Used Hours",
      value: (m) =>
        m.isFullPastWeek
          ? fmtHours(m.usedHoursSoFar)
          : `${fmtHoursShort(m.usedHoursSoFar)} / ${fmtHours(
              m.projectedUsedHours
            )}`,
      sub: (m) =>
        m.isFullPastWeek
          ? fmtSignedPctVs(m.usedVsScheduledVariance, "scheduled")
          : `${fmtSignedPctVs(
              m.usedVsScheduledVariance,
              "scheduled"
            )} (projected)`,
      subColorClass: (m) => costVarianceColorClass(m.usedVsScheduledVariance),
      sparkline: (m) => m.dailyHybridUsedHours,
      sparklineType: "line",
      sparklineTooltip: {
        kind: "value",
        formatter: (m, i) => fmtHours(m.dailyHybridUsedHours[i]),
        suffix: "used",
      },
      hasPastBoundary: true,
    },
  },

  hours_forecasted: {
    id: "hours_forecasted",
    label: "Forecasted vs Earned Hours",
    icon: GroupsIcon,
    actualGated: null,
    planned: {
      cardLabel: () => "Forecasted Hours",
      value: (m) => fmtHours(m.weekTargetHours),
      sub: () => "Week Total",
      sparkline: (m) => m.dailyTargetHours,
      sparklineType: "line",
      sparklineTooltip: {
        kind: "value",
        formatter: (m, i) => fmtHours(m.dailyTargetHours[i]),
        suffix: "forecasted",
      },
    },
    actual: {
      cardLabel: () => "Earned Hours",
      value: (m) =>
        m.isFullPastWeek
          ? fmtHours(m.earnedHoursSoFar)
          : `${fmtHoursShort(m.earnedHoursSoFar)} / ${fmtHours(
              m.projectedEarnedHours
            )}`,
      sub: (m) =>
        m.isFullPastWeek
          ? fmtSignedPctVs(m.earnedVsForecastedVariance, "forecast")
          : `${fmtSignedPctVs(
              m.earnedVsForecastedVariance,
              "forecast"
            )} (projected)`,
      subColorClass: (m) => varianceColorClass(m.earnedVsForecastedVariance),
      sparkline: (m) => m.dailyHybridEarnedHours,
      sparklineType: "line",
      sparklineTooltip: {
        kind: "value",
        formatter: (m, i) => fmtHours(m.dailyHybridEarnedHours[i]),
        suffix: "earned",
      },
      hasPastBoundary: true,
    },
  },

  coverage: {
    id: "coverage",
    label: "Coverage",
    icon: ShieldOutlinedIcon,
    actualGated: null,
    planned: {
      cardLabel: () => "Planned Coverage %",
      value: (m) => fmtPct(m.weekCoverage),
      sub: () => "of forecasted",
      sparkline: (m) => m.dailyCoverage,
      sparklineType: "line",
      sparklineReferenceValue: 100,
      sparklineTooltip: {
        kind: "value",
        formatter: (m, i) => fmtPct(m.dailyCoverage[i]),
        suffix: "planned",
      },
    },
    actual: {
      cardLabel: () => "Actual Coverage %",
      value: (m) => fmtPct(m.weekActualCoverage),
      sub: () => "of earned",
      subColorClass: (m) =>
        m.weekActualCoverage > 100 ? "text-rose-600" : "text-emerald-600",
      sparkline: (m) => m.dailyHybridCoverage,
      sparklineType: "line",
      sparklineReferenceValue: 100,
      sparklineTooltip: {
        kind: "value",
        formatter: (m, i) => fmtPct(m.dailyHybridCoverage[i]),
        suffix: "actual",
      },
      hasPastBoundary: true,
    },
  },

  violations: {
    id: "violations",
    label: "Labor Law Violations",
    icon: WarningAmberIcon,
    actualGated: "laborLawEnabled",
    planned: {
      cardLabel: (m, ctx) =>
        ctx.showLaborLawCard ? "Scheduled Law Violations" : "Conflicts",
      value: (m, ctx) =>
        ctx.showLaborLawCard
          ? ctx.laborLawViolationsCount
          : ctx.conflictsCount,
      sub: (m, ctx) => (ctx.showLaborLawCard ? "From schedule" : "Shifts"),
      sparkline: (m, ctx) =>
        ctx.showLaborLawCard ? m.dailyLaborLawViolations : undefined,
      sparklineType: "bar",
      sparklineTooltip: {
        kind: "violations",
        when: (m, ctx) => ctx.showLaborLawCard,
        details: (m) => m.dailyLaborLawViolationDetails,
      },
    },
    actual: {
      cardLabel: () => "Actual Law Violations",
      value: (m) => m.actualLaborLawViolationsCount,
      sub: () => "From timecards",
      sparkline: (m) => m.dailyActualLaborLawViolations,
      sparklineType: "bar",
      sparklineTooltip: {
        kind: "violations",
        when: () => true,
        details: (m) => m.dailyActualLaborLawViolationDetails,
      },
    },
  },
};

SUMMARY_CARD_REGISTRY.labor_cost = {
  id: "labor_cost",
  label: "Labor Cost",
  icon: PaidOutlinedIcon,
  actualGated: null,
  planned: {
    cardLabel: () => "Scheduled Labor Cost",
    value: (m) => fmtMoney(m.weekLaborCost),
    sub: () => "Week Total",
    sparkline: (m) => m.dailyLaborCost,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtMoney(m.dailyLaborCost[i]),
      suffix: "scheduled",
    },
  },
  actual: {
    cardLabel: () => "Actual Labor Cost",
    value: (m) =>
      m.isFullPastWeek
        ? fmtMoney(m.actualLaborCostSoFar)
        : `${fmtMoney(m.actualLaborCostSoFar)} / ${fmtMoney(
            m.projectedLaborCost
          )}`,
    sub: (m) =>
      m.isFullPastWeek
        ? fmtSignedPctVs(m.laborCostVariance, "scheduled")
        : `${fmtSignedPctVs(m.laborCostVariance, "scheduled")} (projected)`,
    subColorClass: (m) => costVarianceColorClass(m.laborCostVariance),
    sparkline: (m) => m.dailyHybridLaborCost,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtMoney(m.dailyHybridLaborCost[i]),
      suffix: "actual",
    },
    hasPastBoundary: true,
  },
};

SUMMARY_CARD_REGISTRY.labor_pct = {
  id: "labor_pct",
  label: "Labor % of Sales",
  icon: PercentOutlinedIcon,
  actualGated: null,
  planned: {
    cardLabel: () => "Scheduled Labor %",
    value: (m) => fmtPct(m.laborPctSales),
    sub: () => "of forecasted sales",
    sparkline: (m) => m.dailyLaborPct,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtPct(m.dailyLaborPct[i]),
      suffix: "scheduled",
    },
  },
  actual: {
    cardLabel: () => "Used Labor %",
    value: (m) =>
      m.isFullPastWeek || m.hasClosedDays
        ? fmtPct(m.actualLaborPctSoFar)
        : `${fmtPct(m.actualLaborPctSoFar)} / ${fmtPct(m.projectedLaborPct)}`,
    sub: (m) => {
      if (m.isFullPastWeek)
        return fmtSignedPp(m.laborPctPpVariance, "scheduled");
      if (!m.hasClosedDays)
        return `${fmtSignedPp(m.laborPctPpVariance, "scheduled")} (projected)`;
      return `${fmtSignedPp(
        m.laborPctSoFarPpVariance,
        "scheduled"
      )} (so far)`;
    },
    subColorClass: (m) =>
      costVarianceColorClass(
        m.isFullPastWeek || !m.hasClosedDays
          ? m.laborPctPpVariance
          : m.laborPctSoFarPpVariance
      ),
    sparkline: (m) => m.dailyHybridLaborPct,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtPct(m.dailyHybridLaborPct[i]),
      suffix: "used",
    },
    hasPastBoundary: true,
  },
};

SUMMARY_CARD_REGISTRY.transactions = {
  id: "transactions",
  label: "Transactions",
  icon: ReceiptLongOutlinedIcon,
  actualGated: null,
  planned: {
    cardLabel: () => "Forecasted Transactions",
    value: (m) => fmtNumber(m.weekForecastedTransactions),
    sub: () => "Week Total",
    sparkline: (m) => m.dailyForecastedTransactions,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtNumber(m.dailyForecastedTransactions[i]),
      suffix: "forecasted",
    },
  },
  actual: {
    cardLabel: () => "Actual Transactions",
    value: (m) =>
      m.isFullPastWeek
        ? fmtNumber(m.actualTransactionsSoFar)
        : `${fmtNumber(m.actualTransactionsSoFar)} / ${fmtNumber(
            m.projectedTransactions
          )}`,
    sub: (m) =>
      m.isFullPastWeek
        ? fmtSignedPct(m.transactionsVariance)
        : `${fmtSignedPct(m.transactionsVariance)} (projected)`,
    subColorClass: (m) => varianceColorClass(m.transactionsVariance),
    sparkline: (m) => m.dailyHybridTransactions,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtNumber(m.dailyHybridTransactions[i]),
      suffix: "actual",
    },
    hasPastBoundary: true,
  },
};

SUMMARY_CARD_REGISTRY.avg_check = {
  id: "avg_check",
  label: "Avg Check Size",
  icon: RestaurantOutlinedIcon,
  actualGated: null,
  planned: {
    cardLabel: () => "Forecasted Avg Check",
    value: (m) => fmtMoneyCents(m.weekAvgCheck),
    sub: () => "Week Avg",
    sparkline: (m) => m.dailyAvgCheck,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtMoneyCents(m.dailyAvgCheck[i]),
      suffix: "forecasted",
    },
  },
  actual: {
    cardLabel: () => "Actual Avg Check",
    value: (m) =>
      m.isFullPastWeek
        ? fmtMoneyCents(m.actualAvgCheckSoFar)
        : `${fmtMoneyCents(m.actualAvgCheckSoFar)} / ${fmtMoneyCents(
            m.projectedAvgCheck
          )}`,
    sub: (m) =>
      m.isFullPastWeek
        ? fmtSignedPct(m.avgCheckVariance)
        : `${fmtSignedPct(m.avgCheckVariance)} (projected)`,
    subColorClass: (m) => varianceColorClass(m.avgCheckVariance),
    sparkline: (m) => m.dailyHybridAvgCheck,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtMoneyCents(m.dailyHybridAvgCheck[i]),
      suffix: "actual",
    },
    hasPastBoundary: true,
  },
};

SUMMARY_CARD_REGISTRY.splh = {
  id: "splh",
  label: "Sales per Labor Hour",
  icon: TrendingUpIcon,
  actualGated: null,
  planned: {
    cardLabel: () => "Scheduled SPLH",
    value: (m) => fmtSplh(m.weekSPLH),
    sub: () => "Week Avg",
    sparkline: (m) => m.dailySPLH,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtSplh(m.dailySPLH[i]),
      suffix: "scheduled",
    },
  },
  actual: {
    cardLabel: () => "Used SPLH",
    value: (m) =>
      m.isFullPastWeek || m.hasClosedDays
        ? fmtSplh(m.actualSPLHSoFar)
        : `${fmtSplh(m.actualSPLHSoFar)} / ${fmtSplh(m.projectedSPLH)}`,
    sub: (m) => {
      if (m.isFullPastWeek)
        return fmtSignedPctVs(m.splhVariance, "scheduled");
      if (!m.hasClosedDays)
        return `${fmtSignedPctVs(m.splhVariance, "scheduled")} (projected)`;
      return `${fmtSignedPctVs(m.splhSoFarVariance, "scheduled")} (so far)`;
    },
    subColorClass: (m) =>
      varianceColorClass(
        m.isFullPastWeek || !m.hasClosedDays
          ? m.splhVariance
          : m.splhSoFarVariance
      ),
    sparkline: (m) => m.dailyHybridSPLH,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtSplh(m.dailyHybridSPLH[i]),
      suffix: "used",
    },
    hasPastBoundary: true,
  },
};

SUMMARY_CARD_REGISTRY.tplh = {
  id: "tplh",
  label: "Transactions per Labor Hour",
  icon: SpeedOutlinedIcon,
  actualGated: null,
  planned: {
    cardLabel: () => "Scheduled TPLH",
    value: (m) => fmtTplh(m.weekTPLH),
    sub: () => "Week Avg",
    sparkline: (m) => m.dailyTPLH,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtTplh(m.dailyTPLH[i]),
      suffix: "scheduled",
    },
  },
  actual: {
    cardLabel: () => "Used TPLH",
    value: (m) =>
      m.isFullPastWeek || m.hasClosedDays
        ? fmtTplh(m.actualTPLHSoFar)
        : `${fmtTplh(m.actualTPLHSoFar)} / ${fmtTplh(m.projectedTPLH)}`,
    sub: (m) => {
      if (m.isFullPastWeek)
        return fmtSignedPctVs(m.tplhVariance, "scheduled");
      if (!m.hasClosedDays)
        return `${fmtSignedPctVs(m.tplhVariance, "scheduled")} (projected)`;
      return `${fmtSignedPctVs(m.tplhSoFarVariance, "scheduled")} (so far)`;
    },
    subColorClass: (m) =>
      varianceColorClass(
        m.isFullPastWeek || !m.hasClosedDays
          ? m.tplhVariance
          : m.tplhSoFarVariance
      ),
    sparkline: (m) => m.dailyHybridTPLH,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtTplh(m.dailyHybridTPLH[i]),
      suffix: "used",
    },
    hasPastBoundary: true,
  },
};

// SUMMARY_CARD_REGISTRY.open_shifts = {
//   id: "open_shifts",
//   label: "Open Shifts",
//   icon: PersonSearchOutlinedIcon,
//   actualGated: null,
//   planned: {
//     cardLabel: () => "Open Shifts",
//     value: (m, ctx) => fmtNumber(ctx.openShiftsCount),
//     sub: () => "Need filling",
//     sparkline: (m) => m.dailyOpen,
//     sparklineType: "bar",
//     sparklineTooltip: {
//       kind: "value",
//       formatter: (m, i) => fmtNumber(m.dailyOpen[i]),
//       suffix: "open",
//     },
//   },
//   actual: null,
// };

SUMMARY_CARD_REGISTRY.avg_shift_length = {
  id: "avg_shift_length",
  label: "Avg Shift Length",
  icon: TimerOutlinedIcon,
  actualGated: null,
  planned: {
    cardLabel: () => "Scheduled Avg Shift",
    value: (m) => fmtHours(m.weekAvgShiftLength),
    sub: () => "Week Avg",
    sparkline: (m) => m.dailyAvgShiftLength,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtHours(m.dailyAvgShiftLength[i]),
      suffix: "scheduled",
    },
  },
  actual: {
    cardLabel: () => "Actual Avg Shift",
    value: (m) =>
      m.isFullPastWeek
        ? fmtHours(m.actualAvgShiftLength)
        : `${fmtHours(m.actualAvgShiftLength)} / ${fmtHours(
            m.projectedAvgShiftLength
          )}`,
    sub: (m) =>
      m.isFullPastWeek
        ? fmtSignedPctVs(m.avgShiftLengthVariance, "scheduled")
        : `${fmtSignedPctVs(m.avgShiftLengthVariance, "scheduled")} (projected)`,
    subColorClass: () => "text-slate-400",
    sparkline: (m) => m.dailyHybridAvgShiftLength,
    sparklineType: "line",
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtHours(m.dailyHybridAvgShiftLength[i]),
      suffix: "actual",
    },
    hasPastBoundary: true,
  },
};

SUMMARY_CARD_REGISTRY.break_compliance = {
  id: "break_compliance",
  label: "Break Compliance %",
  icon: FreeBreakfastOutlinedIcon,
  actualGated: "laborLawEnabled",
  featureFlag: "laborLawEnabled",
  planned: {
    cardLabel: () => "Scheduled Break Compliance %",
    value: (m) => fmtPct(m.plannedBreakCompliancePct),
    sub: () => "of scheduled shifts",
    sparkline: (m) => m.dailyPlannedBreakCompliance,
    sparklineType: "line",
    sparklineReferenceValue: 100,
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtPct(m.dailyPlannedBreakCompliance[i]),
      suffix: "scheduled",
    },
  },
  actual: {
    cardLabel: () => "Actual Break Compliance %",
    value: (m) => fmtPct(m.actualBreakCompliancePct),
    sub: () => "of timecards",
    subColorClass: (m) =>
      m.actualBreakCompliancePct >= 95
        ? "text-emerald-600"
        : m.actualBreakCompliancePct >= 80
        ? "text-slate-400"
        : "text-rose-600",
    sparkline: (m) => m.dailyActualBreakCompliance,
    sparklineType: "line",
    sparklineReferenceValue: 100,
    sparklineTooltip: {
      kind: "value",
      formatter: (m, i) => fmtPct(m.dailyActualBreakCompliance[i]),
      suffix: "actual",
    },
    hasPastBoundary: true,
  },
};

SUMMARY_CARD_REGISTRY.availability_conflicts = {
  id: "availability_conflicts",
  label: "Availability Conflicts",
  icon: EventBusyOutlinedIcon,
  actualGated: null,
  planned: {
    cardLabel: () => "Availability Conflicts",
    value: (m, ctx) => fmtNumber(ctx.conflictsCount),
    sub: () => "Shifts",
    sparkline: (m) => m.dailyConflicts,
    sparklineType: "bar",
    sparklineTooltip: {
      kind: "violations",
      when: () => true,
      details: (m) => m.dailyConflictDetails,
    },
  },
  actual: null,
};

export const DEFAULT_CARD_ORDER = [
  "sales",
  "hours_scheduled",
  "hours_forecasted",
  "coverage",
  "violations",
];

export function sanitizeCardOrder(saved) {
  if (!Array.isArray(saved)) return DEFAULT_CARD_ORDER;
  const seen = new Set();
  const valid = [];
  saved.forEach((id) => {
    if (typeof id !== "string") return;
    if (!SUMMARY_CARD_REGISTRY[id]) return;
    if (seen.has(id)) return;
    seen.add(id);
    valid.push(id);
  });
  if (valid.length === 5) return valid;
  for (const id of DEFAULT_CARD_ORDER) {
    if (valid.length >= 5) break;
    if (!seen.has(id)) {
      seen.add(id);
      valid.push(id);
    }
  }
  return valid.slice(0, 5);
}
